import { Icons } from "assets";
import classNames from "classnames";
import { useEffect, useState } from "react";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import ProductDetailsModal from "shared/modal/productDetailsModal";
import {
  GetAllProductsService,
  SuspendProductService,
  UnSuspendProductService,
} from "shared/services/products";
import { ProductType } from "shared/utils/enum";
import { handleErrors } from "shared/utils/helper";
import styles from "./style.module.scss";

const ProductListings = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [products, setProducts] = useState<ProductDetail[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductDetail | null>(
    null
  );
  const [suspendLoading, setSuspendLoading] = useState<boolean>(false);

  const handleShowConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const handleShowDetailModal = () => {
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const tableHeads = [
    { title: "Title", key: "title" },
    { title: "Type", key: "type" },
    { title: "Price", key: "Price" },
    { title: "Status", key: "status" },
    { title: "Action", key: "action" },
  ];

  const handleGetAllUsers = async (page = 1) => {
    try {
      setLoading(true);
      const response = await GetAllProductsService(page, 10);
      setProducts(response.data.data.data || []);
      setTotalItems(response.data.data.meta.totalItems || 0);
    } catch (error) {
      console.error("Error fetching users:", error);
      toastMessage("error", "Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendUser = async () => {
    if (!selectedProduct) return;

    setSuspendLoading(true);
    if (selectedProduct?.is_suspended) {
      UnSuspendProductService(selectedProduct.id)
        .then(({ data, status }) => {
          if (status) {
            toastMessage("success", "Product Activated Successfully");
            handleGetAllUsers(currentPage);
            handleCloseConfirmationModal();
          }
        })
        .catch((error) => {
          handleErrors(error);
        })
        .finally(() => {
          setSuspendLoading(false);
        });
    } else {
      SuspendProductService(selectedProduct.id)
        .then(({ data, status }) => {
          if (status) {
            toastMessage("success", "Product Suspended Successfully");
            handleGetAllUsers(currentPage);
            handleCloseConfirmationModal();
          }
        })
        .catch((error) => {
          handleErrors(error);
        })
        .finally(() => {
          setSuspendLoading(false);
        });
    }
  };

  useEffect(() => {
    handleGetAllUsers(currentPage);
  }, [currentPage]);

  return (
    <div className={styles.sectionContainer}>
      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={tableHeads}
          title="Products List"
          isEmpty={!loading && !products.length}
        >
          {loading
            ? Array.from({ length: 2 }).map((_, index) => (
                <TableRowLoader key={index} />
              ))
            : products.map((data, idx) => (
                <tr key={data.id}>
                  <td>{data.title}</td>
                  <td>{data.product_type}</td>
                  <td>
                    {data.product_type === ProductType.ISO
                      ? `$${data.price} - $${data.priceUpto}`
                      : `$${data.price}`}
                  </td>
                  <td>{data.is_suspended ? "Suspended" : "Active"}</td>
                  <td className="">
                    <div className={classNames(styles.IconsContainer)}>
                      <CustomToolTip label="View">
                        <div
                          onClick={() => {
                            setSelectedProduct(data);
                            handleShowDetailModal();
                          }}
                          className={classNames(styles.actionContainer)}
                        >
                          <Icons.Eye />
                        </div>
                      </CustomToolTip>
                      {data.is_suspended ? (
                        <CustomToolTip label="Activate">
                          <div
                            onClick={() => {
                              setSelectedProduct(data);
                              handleShowConfirmationModal();
                            }}
                            className={classNames(styles.actionContainer)}
                          >
                            <Icons.Tick className={classNames(styles.accept)} />
                          </div>
                        </CustomToolTip>
                      ) : (
                        <CustomToolTip label="Suspend">
                          <div
                            onClick={() => {
                              setSelectedProduct(data);
                              handleShowConfirmationModal();
                            }}
                            className={classNames(styles.actionContainer)}
                          >
                            <Icons.Cross />
                          </div>
                        </CustomToolTip>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
        </CustomTable>
      </div>

      <CustomPagination
        onPageChange={handlePageChange}
        totalCount={totalItems}
        currentPage={currentPage}
        pageSize={10}
      />

      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={handleCloseConfirmationModal}
        handleSubmit={handleSuspendUser}
        loading={suspendLoading}
      />
      <ProductDetailsModal
        show={showDetailModal}
        handleClose={handleCloseDetailModal}
        detail={selectedProduct}
      />
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 5 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default ProductListings;
